import type {
  Account,
  AccountGroup,
  Category,
  CorePayload,
  Id,
  SearchableTransaction,
  Transaction,
  YearMonth,
} from './types';

export function normalizeSearch(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('it')
    .trim();
}

export class DataStore {
  readonly core: CorePayload;
  readonly accountById: ReadonlyMap<Id, Account>;
  readonly groupById: ReadonlyMap<Id, AccountGroup>;
  readonly categoryById: ReadonlyMap<Id, Category>;
  readonly days: readonly string[];
  readonly months: readonly YearMonth[];
  transactions: readonly SearchableTransaction[] = [];

  constructor(core: CorePayload) {
    const accountGroups = core.account_groups.filter((group) => group.is_deleted === 0);
    const activeGroupIds = new Set(accountGroups.map((group) => group.id));

    const activeAccounts = core.accounts.filter(
      (account) => account.is_deleted !== 1 && activeGroupIds.has(account.group_id),
    );
    const activeAccountIds = new Set(activeAccounts.map((account) => account.id));
    const accounts = activeAccounts.map((account) => ({
      ...account,
      card_of:
        account.card_of !== null && activeAccountIds.has(account.card_of) ? account.card_of : null,
    }));

    const activeCategories = core.categories.filter((category) => category.is_deleted === 0);
    const activeCategoryIds = new Set(activeCategories.map((category) => category.id));
    const categories = activeCategories.map((category) => ({
      ...category,
      parent_id:
        category.parent_id !== null && activeCategoryIds.has(category.parent_id)
          ? category.parent_id
          : null,
    }));

    this.core = {
      ...core,
      account_groups: accountGroups,
      accounts,
      categories,
      account_month: core.account_month.filter(
        (row) => activeAccountIds.has(row.account_id) && activeGroupIds.has(row.account_group_id),
      ),
    };
    this.accountById = new Map(accounts.map((item) => [item.id, item]));
    this.groupById = new Map(accountGroups.map((item) => [item.id, item]));
    this.categoryById = new Map(categories.map((item) => [item.id, item]));
    this.days = this.core.calendar.map((day) => day.date);
    this.months = Array.from(new Set(this.core.calendar.map((day) => day.month)));
  }

  setTransactions(transactions: readonly Transaction[]): void {
    if (this.transactions.length > 0) return;
    this.transactions = transactions
      .filter(
        (transaction) =>
          this.accountById.has(transaction.account_id) &&
          this.groupById.has(transaction.account_group_id),
      )
      .map((transaction) => ({
        ...transaction,
        counter_account_id:
          transaction.counter_account_id !== null &&
          this.accountById.has(transaction.counter_account_id)
            ? transaction.counter_account_id
            : null,
        category_id:
          transaction.category_id !== null && this.categoryById.has(transaction.category_id)
            ? transaction.category_id
            : null,
        parent_category_id:
          transaction.parent_category_id !== null &&
          this.categoryById.has(transaction.parent_category_id)
            ? transaction.parent_category_id
            : null,
        rollup_category_id:
          transaction.rollup_category_id !== null &&
          this.categoryById.has(transaction.rollup_category_id)
            ? transaction.rollup_category_id
            : null,
        descriptionSearch: normalizeSearch(transaction.description),
      }));
  }

  accountLabel(id: Id): string {
    return this.accountById.get(id)?.name ?? 'Conto sconosciuto';
  }

  groupLabel(id: Id): string {
    return this.groupById.get(id)?.name ?? 'Gruppo sconosciuto';
  }

  categoryLabel(id: Id | null): string {
    return id ? (this.categoryById.get(id)?.name ?? 'Categoria sconosciuta') : 'Senza categoria';
  }
}
